import { useLazyQuery } from '@apollo/client'
import { QUERY_HUBSPOT_ACCESS_TOKEN } from 'components/Layout/LayoutQueries'
import { useEffect, useRef, useState } from 'react'
import jwt from 'jsonwebtoken'

declare global {
  interface Window {
    hsConversationsSettings?: {
      loadImmediately: boolean
    }
  }
}

interface User {
  id: string
  api_key: string
  email: string
  firstname: string
  lastname: string
  role: string
}

interface HubspotJWT {
  aud: string
  exp: number
  first_name: string
  iat: number
  last_name: string
  sub: string
}

interface HubspotData {
  getHubspotAccessToken: string
}

export const useHubspotChat = (user: User) => {
  // hsConversationsSettings needs to be set prior to the call, otherwise we may experience a race condition that interferes with widget load
  const settingsInitializedRef = useRef(false)
  if (!settingsInitializedRef.current) {
    window.hsConversationsSettings = {
      loadImmediately: false
    }
    settingsInitializedRef.current = true
  }

  const refreshTimerRef = useRef<number | null>(null)

  const [getHubspotAccessToken, { error }] = useLazyQuery(
    QUERY_HUBSPOT_ACCESS_TOKEN,
    {
      onCompleted: (data: HubspotData) => {
        const { getHubspotAccessToken: accessToken } = data
        setHubspotJWT(accessToken)
        scheduleRefresh(accessToken)
      },
      fetchPolicy: 'no-cache'
    }
  )
  const [hubspotJWT, setHubspotJWT] = useState<string | null>(null)

  const scheduleRefresh = (token: string) => {
    try {
      const decoded = jwt.decode(token) as HubspotJWT
      const expirationDateMs = (decoded?.exp || 0) * 1000
      // Refresh 60s before expiry to avoid race conditions
      const refreshDelayMs = Math.max(expirationDateMs - Date.now() - 60_000, 0)

      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
      }

      if (!expirationDateMs || refreshDelayMs === 0) {
        getHubspotAccessToken({
          variables: { userId: user.id }
        })
      } else {
        refreshTimerRef.current = window.setTimeout(() => {
          getHubspotAccessToken({
            variables: { userId: user.id }
          })
        }, refreshDelayMs)
      }
    } catch (e) {
      // If decoding fails, fall back to immediate refresh
      getHubspotAccessToken({
        variables: { userId: user.id }
      })
    }
  }

  useEffect(() => {
    if (!user?.id) return
    // Always fetch a fresh token on mount/user change
    getHubspotAccessToken({
      variables: { userId: user.id }
    })

    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [user?.id])

  if (error)
    return console.error(`Unable to get hubspot token due to: ${error}`)

  return hubspotJWT
}
